import { DashboardState } from "@kuika/kuika-cl-model";
import { PureComponent } from "react";
import { DesignerMessageSender } from "../../messages/designer-message-sender";
import { FrameMessageReceiver } from "../../messages/frame-message-receiver";
import { stopDesignerLive } from "../../services/designer-live/designer-live";
import { ScreenService } from "../../services/screen-service";
import { IWithContextProp } from "../../shared/hoc/with-context";
import { KNavigator } from "../../shared/hoc/with-history";
import { AiImageToScreenGenerator } from "../../shared/utility/ai-image-to-screen-generation-helper";
import { DragDrop } from "../../shared/utility/drag-drop-helper";
import { PropertyHelper } from "../../shared/utility/property-helper";
import { PropertySdHelper } from "../../shared/utility/property-sd-helper";
import { StartProcessTriggerSync } from "../../shared/utility/start-process-trigger-sync";
import { CustomBlocksProvider } from "./custom-blocks-provider";
import { trackDesignerTeardown, whenPreviousDesignerTornDown } from "./designer-teardown-gate";
import { AiActionProvider } from "./provider-partitions/ai-action-provider";
import { ApiManagementProvider } from "./provider-partitions/api-management-provider";
import { BluetoothMethodProvider } from "./provider-partitions/bluetooth-method-provider";
import { BranchingProvider } from "./provider-partitions/branching-provider";
import { CollabProvider } from "./provider-partitions/collab-provider";
import { ConfigManagerProvider } from "./provider-partitions/config-manager-provider";
import { DatasourceProvider } from "./provider-partitions/datasource-provider";
import { DefaultStyleProvider } from "./provider-partitions/default-style-provider";
import { DesignSituationProvider } from "./provider-partitions/design-situation-provider";
import { DesignTimeElementStateProvider } from "./provider-partitions/design-time-element-state-provider";
import { DisplayItemProvider } from "./provider-partitions/display-item-provider";
import { DocumentParametersProvider } from "./provider-partitions/document-parameters-provider";
import { DocumentsProvider } from "./provider-partitions/documents-provider";
import { DynamicTypesProvider } from "./provider-partitions/dynamic-types-provider";
import { ExceptionHandleProvider } from "./provider-partitions/exception-handle-provider";
import { FolderProvider } from "./provider-partitions/folder-provider";
import { FrameProvider } from "./provider-partitions/frame-provider";
import { HelpdeskProvider } from "./provider-partitions/helpdesk-provider";
import { InvisibleStylesProvider } from "./provider-partitions/invisible-styles-provider";
import { NavigateProvider } from "./provider-partitions/navigate-provider";
import { NotificationTemplateProvider } from "./provider-partitions/notification-template-provider";
import { ProjectColorsProvider } from "./provider-partitions/project-colors-provider";
import { ProjectProvider } from "./provider-partitions/project-provider";
import { PropertyProvider } from "./provider-partitions/property-provider";
import { QueryBuilderProvider } from "./provider-partitions/query-builder-provider";
import { ReportDesignProvider } from "./provider-partitions/report-design-provider";
import { ResponsiveProvider } from "./provider-partitions/responsive-provider";
import { ScheduledJobsProvider } from "./provider-partitions/scheduled-jobs-provider";
import { ScreenAuthorizationProvider } from "./provider-partitions/screen-authorization-provider";
import { ScreenProvider } from "./provider-partitions/screen-provider";
import { WizardProvider } from "./provider-partitions/wizard-provider";
import { WorkflowCollabProvider } from "./provider-partitions/workflow-collab-provider";
import { WorkflowProvider } from "./provider-partitions/workflow-provider";
import { ZendeskProvider } from "./provider-partitions/zendesk-provider";
import {
  DefaultScreenDesignContextState,
  IScreenDesignContextState,
  ScreenDesignContext
} from "./screen-design-context";

export interface IDragDropEvent {
  detail: {
    action: string;
    reason: string;
    source: { name: string; id: string; kuikaid: string };
    target: { kuikaid: string; colsNumber?: number[] };
  };
}

export interface ISelectedDisplayItemInfo {
  kuikaId: string;
}

export interface IScreenDesignContextProviderProps {
  navigator: KNavigator;
  children: React.ReactNode;
}

declare let window: any;
export class ScreenDesignContextProvider extends PureComponent<
  IScreenDesignContextProviderProps & IWithContextProp,
  IScreenDesignContextState
> {
  public designSituationProvider: DesignSituationProvider;

  public projectProvider: ProjectProvider;

  public frameProvider: FrameProvider;

  public screenProvider: ScreenProvider;

  public displayItemProvider: DisplayItemProvider;

  public propertyProvider: PropertyProvider;

  public responsiveProvider: ResponsiveProvider;

  public documentsProvider: DocumentsProvider;

  public navigateProvider: NavigateProvider;

  public exceptionHandleProvider: ExceptionHandleProvider;

  public screenAuthorizationProvider: ScreenAuthorizationProvider;

  public workflowProvider: WorkflowProvider;

  public zendeskProvider: ZendeskProvider;

  public scheduledJobsProvider: ScheduledJobsProvider;

  public documentParametersProvider: DocumentParametersProvider;

  public apiManagementProvider: ApiManagementProvider;

  public reportDesignProvider: ReportDesignProvider;

  public wizardProvider: WizardProvider;

  public configManagerProvider: ConfigManagerProvider;

  public invisibleStylesProvider: InvisibleStylesProvider;

  public defaultStyleProvider: DefaultStyleProvider;

  public dynamicTypesProvider: DynamicTypesProvider;

  public folderProvider: FolderProvider;

  public datasourceProvider: DatasourceProvider;

  public queryBuilderProvider: QueryBuilderProvider;

  public notificationTemplateProvider: NotificationTemplateProvider;

  public bluetoothMethodProvider: BluetoothMethodProvider;

  public projectColorsProvider: ProjectColorsProvider;

  public collabProvider: CollabProvider;

  public workflowCollabProvider: WorkflowCollabProvider;

  public helpdeskProvider: HelpdeskProvider;

  public aiActionProvider: AiActionProvider;

  public designTimeElementStateProvider: DesignTimeElementStateProvider;

  dragDrop: DragDrop;

  propertyHelper: PropertyHelper;

  propertySdHelper: PropertySdHelper;

  frameMessageReceiver: FrameMessageReceiver = new FrameMessageReceiver(this);

  frame: DesignerMessageSender;

  public branchingProvider: BranchingProvider;

  aiImageToScreenGenerator: AiImageToScreenGenerator;

  customBlocksProvider: CustomBlocksProvider;

  startProcessTriggerSync: StartProcessTriggerSync;

  constructor(props: IScreenDesignContextProviderProps & IWithContextProp) {
    super(props);
    const dashboardState = DashboardState.design;
    this.frame = new DesignerMessageSender();
    const defaultState = DefaultScreenDesignContextState;
    defaultState.dashboardState = dashboardState;
    this.state = defaultState;

    this.startProcessTriggerSync = new StartProcessTriggerSync(this);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues, this);
    this.propertyHelper = new PropertyHelper();
    this.dragDrop = new DragDrop(this);

    // Partial Providers
    this.designSituationProvider = new DesignSituationProvider(this);
    this.projectProvider = new ProjectProvider(this);
    this.frameProvider = new FrameProvider(this);
    this.screenProvider = new ScreenProvider(this);
    this.displayItemProvider = new DisplayItemProvider(this);
    this.propertyProvider = new PropertyProvider(this);
    this.responsiveProvider = new ResponsiveProvider(this);
    this.documentsProvider = new DocumentsProvider(this);
    this.navigateProvider = new NavigateProvider(this);
    this.exceptionHandleProvider = new ExceptionHandleProvider(this);
    this.screenAuthorizationProvider = new ScreenAuthorizationProvider(this);
    this.workflowProvider = new WorkflowProvider(this);
    this.zendeskProvider = new ZendeskProvider(this);
    this.scheduledJobsProvider = new ScheduledJobsProvider(this);
    this.documentParametersProvider = new DocumentParametersProvider(this);
    this.apiManagementProvider = new ApiManagementProvider(this);
    this.reportDesignProvider = new ReportDesignProvider(this);
    this.wizardProvider = new WizardProvider(this);
    this.aiImageToScreenGenerator = new AiImageToScreenGenerator(this);
    this.configManagerProvider = new ConfigManagerProvider(this);
    this.branchingProvider = new BranchingProvider(this);
    this.invisibleStylesProvider = new InvisibleStylesProvider(this);
    this.defaultStyleProvider = new DefaultStyleProvider(this);
    this.dynamicTypesProvider = new DynamicTypesProvider(this);
    this.folderProvider = new FolderProvider(this);
    this.datasourceProvider = new DatasourceProvider(this);
    this.queryBuilderProvider = new QueryBuilderProvider(this);
    this.notificationTemplateProvider = new NotificationTemplateProvider(this);
    this.bluetoothMethodProvider = new BluetoothMethodProvider(this);
    this.projectColorsProvider = new ProjectColorsProvider(this);
    this.collabProvider = new CollabProvider(this);
    this.workflowCollabProvider = new WorkflowCollabProvider(this);
    this.helpdeskProvider = new HelpdeskProvider(this);
    this.aiActionProvider = new AiActionProvider(this);
    this.customBlocksProvider = new CustomBlocksProvider(this);
    this.designTimeElementStateProvider = new DesignTimeElementStateProvider(this);
  }

  getScreenDesignContextProviderValues = () => {
    return {
      ...this.designSituationProvider.getProviderValues(),
      ...this.projectProvider.getProviderValues(),
      ...this.frameProvider.getProviderValues(),
      ...this.screenProvider.getProviderValues(),
      ...this.displayItemProvider.getProviderValues(),
      ...this.propertyProvider.getProviderValues(),
      ...this.responsiveProvider.getProviderValues(),
      ...this.documentsProvider.getProviderValues(),
      ...this.navigateProvider.getProviderValues(),
      ...this.exceptionHandleProvider.getProviderValues(),
      ...this.screenAuthorizationProvider.getProviderValues(),
      ...this.workflowProvider.getProviderValues(),
      ...this.zendeskProvider.getProviderValues(),
      ...this.scheduledJobsProvider.getProviderValues(),
      ...this.documentParametersProvider.getProviderValues(),
      ...this.apiManagementProvider.getProviderValues(),
      ...this.reportDesignProvider.getProviderValues(),
      ...this.wizardProvider.getProviderValues(),
      ...this.configManagerProvider.getProviderValues(),
      ...this.branchingProvider.getProviderValues(),
      ...this.invisibleStylesProvider.getProviderValues(),
      ...this.defaultStyleProvider.getProviderValues(),
      ...this.dynamicTypesProvider.getProviderValues(),
      ...this.folderProvider.getProviderValues(),
      ...this.datasourceProvider.getProviderValues(),
      ...this.queryBuilderProvider.getProviderValues(),
      ...this.notificationTemplateProvider.getProviderValues(),
      ...this.bluetoothMethodProvider.getProviderValues(),
      ...this.projectColorsProvider.getProviderValues(),
      ...this.collabProvider.getProviderValues(),
      ...this.workflowCollabProvider.getProviderValues(),
      ...this.helpdeskProvider.getProviderValues(),
      ...this.aiActionProvider.getProviderValues(),
      ...this.customBlocksProvider.getProviderValues(),
      ...this.designTimeElementStateProvider.getProviderValues()
    };
  };

  render() {
    const { children } = this.props;
    return (
      <div style={{ height: "100%" }}>
        <ScreenDesignContext.Provider value={this.getScreenDesignContextProviderValues()}>
          {children}
        </ScreenDesignContext.Provider>
      </div>
    );
  }

  componentWillUnmount() {
    this.hasUnmounted = true;
    // Tracked so a provider mounted in the same commit (another app opened from the header's
    // app switcher) loads only after this save and teardown are done — see designer-teardown-gate.
    trackDesignerTeardown(this.tearDown(whenPreviousDesignerTornDown()));
  }

  private hasUnmounted: boolean = false;

  /**
   * False once this provider has been replaced. `openProject` checks it after each await: a load
   * that lands after its provider was swapped out (A→B→C faster than B's fetches) must not write
   * B's tree, designer-live or app id over C's.
   */
  isLive = (): boolean => !this.hasUnmounted;

  private tearDown = async (previousTeardown: Promise<void>): Promise<void> => {
    try {
      await previousTeardown;
      // Only a provider whose app finished loading owns what the shared store holds; one replaced
      // mid-load would save an empty or foreign tree (the save's id comes from the tree itself).
      await this.screenProvider.saveScreenOnLeave();
    } finally {
      // Teardown must be unconditional (H4): if saveScreen rejects, the
      // listeners, autosave interval, and store must still be released —
      // otherwise the message/popstate listeners (which close over this
      // provider) stack across screen navigations and retain the whole subtree.
      this.collabProvider.leaveCollabSession();
      this.workflowCollabProvider.leaveWorkflowCollabSession();
      stopDesignerLive();
      if (this.state.sdId !== "0") ScreenService.Leave(this.state.sdId);
      this.screenProvider.terminateSdStore();
      if (this.screenProvider.autoSaveIntervalId) {
        clearInterval(this.screenProvider.autoSaveIntervalId);
      }
      window.removeEventListener("popstate", this.onBackButtonEvent);
      window.removeEventListener("message", this.frameProvider.frameMessage);
      // Release the global back-reference to this provider set in
      // componentDidMount (H3) so the unmounted context graph — all provider
      // partitions + state — can be GC'd. Identity-guarded so a fallback
      // installed elsewhere (e.g. bpmn lint) on another instance is preserved.
      if ((window as unknown as { getSdInstance?: () => unknown }).getSdInstance?.() === this) {
        delete (window as unknown as { getSdInstance?: unknown }).getSdInstance;
      }
      if (process.env.NODE_ENV === "development") {
        delete (window as unknown as { sdContext?: unknown }).sdContext;
      }
    }
  };

  async componentDidMount() {
    window.getSdInstance = () => this;

    // The store and designer-live are shared with the provider this one replaced; its teardown
    // must finish before this app's tree is loaded into them.
    await whenPreviousDesignerTornDown();
    // Replaced again while it waited (a second switch in quick succession): the newer provider
    // loads, this one must not load into the store or leave listeners behind.
    if (this.hasUnmounted) return;
    this.projectProvider.openProject();
    this.configManagerProvider.fetchConfigLocalizations();

    if (window.addEventListener) {
      window.addEventListener("message", this.frameProvider.frameMessage, false);
    } else {
      window.attachEvent("onmessage", this.frameProvider.frameMessage);
    }
    window.addEventListener("popstate", this.onBackButtonEvent);
    // moment.updateLocale("en", {
    //   relativeTime: {
    //     future: "in %s",
    //     past: "%s ago",
    //     s: "secs",
    //     ss: "%d secs",
    //     m: "1 m",
    //     mm: "%d m",
    //     h: "1 h",
    //     hh: "%d h",
    //     d: "1 d",
    //     dd: "%d d",
    //     M: "1 mo",
    //     MM: "%d mo",
    //     y: "1 y",
    //     yy: "%d y"
    //   }
    // });

    if (process.env.NODE_ENV === "development") {
      window.sdContext = { ...this.state };
    }
  }

  componentDidUpdate(
    prevProps: Readonly<IScreenDesignContextProviderProps & IWithContextProp>,
    prevState: Readonly<IScreenDesignContextState>,
    snapshot?: any
  ): void {
    if (process.env.NODE_ENV === "development") {
      window.sdContext = { ...this.state };
    }
  }

  onBackButtonEvent = async (e) => {
    e.preventDefault();
    await this.screenProvider.saveScreen(true);
    this.props.navigator.goBack();
  };

  goBackInHistory(history) {
    history.goBack();
  }
}
